const MIN_BROWSER_VERSION = {
  chrome: "111",
  safari: "16.4",
  firefox: "128",
  edge: "111",
} as const;

function parseVersion(raw: string | undefined) {
  if (!raw) return null;

  const parts = raw.split(".").map((part) => Number.parseInt(part, 10));

  if (parts.length === 0 || parts.some(Number.isNaN)) return null;

  return parts;
}

function compareVersion(a: number[], b: number[]) {
  const length = Math.max(a.length, b.length);

  for (let index = 0; index < length; index++) {
    const left = a[index] ?? 0;
    const right = b[index] ?? 0;

    if (left > right) return 1;
    if (left < right) return -1;
  }

  return 0;
}

function getBrowserVersion() {
  const ua = navigator.userAgent;

  const edge = ua.match(/Edg\/(\d+(?:\.\d+)?)/i);
  if (edge) return { name: "edge" as const, version: parseVersion(edge[1]) };

  const chrome = ua.match(/Chrome\/(\d+(?:\.\d+)?)/i);
  if (chrome && !ua.includes("Edg/"))
    return { name: "chrome" as const, version: parseVersion(chrome[1]) };

  const firefox = ua.match(/Firefox\/(\d+(?:\.\d+)?)/i);
  if (firefox) return { name: "firefox" as const, version: parseVersion(firefox[1]) };

  const safariVersion = ua.match(/Version\/(\d+(?:\.\d+)?)/i);
  const isSafari = /Safari\//i.test(ua) && !/Chrome\//i.test(ua) && !/Chromium\//i.test(ua);
  if (isSafari) return { name: "safari" as const, version: parseVersion(safariVersion?.[1]) };

  return null;
}

function hasTailwindV4FeatureSupport() {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") return false;

  try {
    return (
      CSS.supports("color", "oklch(0.62 0.2 130)") &&
      CSS.supports("selector(:has(*))") &&
      CSS.supports("selector(::backdrop)")
    );
  } catch {
    return null;
  }
}

export function isTailwindV4SupportedBrowser() {
  if (typeof window === "undefined") return true;

  const featureSupport = hasTailwindV4FeatureSupport();
  if (featureSupport === true) return true;

  const browser = getBrowserVersion();
  if (browser?.version) {
    const minVersion = parseVersion(MIN_BROWSER_VERSION[browser.name]);
    if (minVersion && compareVersion(browser.version, minVersion) >= 0) return true;
  }

  if (featureSupport === false) return false;

  // Inconclusive detection (e.g. CSS.supports selector syntax throws in niche environments):
  // prefer allowing access to avoid false negatives on capable browsers.
  return true;
}
